<template>
  <Teleport to="body">
    <div
      v-if="show"
      id="lookbook"
      class="fixed inset-0 z-[99999] bg-white flex items-center justify-center group"
      tabindex="0"
      @click="closeModal"
    >
      <div
        class="flex items-center justify-center w-full h-full relative"
        :class="zoomed ? 'overflow-hidden' : ''"
        @click.stop
      >
        <!-- Video -->
        <video
          v-if="modalVideo"
          :src="modalVideo"
          :class="zoomed ? 'object-none' : 'object-contain max-w-full h-full md:max-h-[80vh]'"
          :style="zoomed ? 'max-width:none;max-height:none;display:block;cursor:zoom-out;margin:auto;' : 'cursor:zoom-in;'"
          autoplay
          muted
          loop
          playsinline
          @click="toggleZoom"
          @contextmenu="handleRightClick"
        />
        <!-- Image avec effet background pour vrai panning -->
        <transition :name="isNavigating ? '' : 'fade'">
          <div
            v-if="!modalVideo"
            class="absolute inset-0 flex items-center justify-center w-full h-full modal-image-container"
            :style="backgroundStyle"
            @mousemove="zoomed ? pan($event) : null"
            @touchmove="zoomed ? panTouch($event) : null"
            @click="toggleZoom"
            @contextmenu="handleRightClick"
          ></div>
        </transition>
      </div>
      <button
        class="fixed pb-1 right-4 w-14 h-14 bg-white bg-opacity-90 rounded-full items-center justify-center text-black text-2xl z-[99999] transition-opacity duration-200 shadow-lg bottom-14 md:top-24 md:bottom-auto"
        style="border-radius: 50%; aspect-ratio: 1; display: flex; align-items: center; justify-content: center; line-height: 1; transform: translateY(-px);"
        @click.stop="closeModal"
      >
        &times;
      </button>
      
      <!-- Navigation Buttons -->
      <button
        class="absolute right-20 bottom-32 md:right-auto md:left-4 md:top-1/2 md:bottom-auto md:-translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-4 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 z-[99999]"
        :aria-label="`Previous image`"
        @click.stop="navigateLookbook('prev')"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
        </svg>
      </button>
      
      <button
        class="absolute right-4 bottom-32 md:right-4 md:top-1/2 md:bottom-auto md:-translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-4 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 z-[99999]"
        :aria-label="`Next image`"
        @click.stop="navigateLookbook('next')"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
        </svg>
      </button>

      <!-- Tips -->
      <div 
        v-if="showTips && !zoomed" 
        class="fixed bottom-4 left-4 bg-black bg-opacity-80 text-white px-4 py-3 rounded-lg z-[99999] transition-opacity duration-300 hidden md:block"
      >
        <div class="flex items-center space-x-4 text-sm">
          <div class="flex items-center space-x-2">
            <span class="font-mono bg-white bg-opacity-20 px-2 py-1 rounded">ESC</span>
            <span class="font-mono bg-white bg-opacity-20 px-2 py-1 rounded">🖱️ R</span>
            <span>Close</span>
          </div>
          <div class="flex items-center space-x-2">
            <span class="font-mono bg-white bg-opacity-20 px-2 py-1 rounded">←</span>
            <span class="font-mono bg-white bg-opacity-20 px-2 py-1 rounded">→</span>
            <span>Navigate</span>
          </div>
          <div class="flex items-center space-x-2">
            <span class="font-mono bg-white bg-opacity-20 px-2 py-1 rounded">Space</span>
            <span class="font-mono bg-white bg-opacity-20 px-2 py-1 rounded">🖱️ L</span>
            <span>Zoom</span>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useGA } from '~/composables/useGA'

interface Props {
  show: boolean
  modalImage: string
  modalZoomImage?: string
  modalVideo?: string
  altText?: string
  productType?: 'pumps' | 'boots' | 'sandals' | 'ankle-boots'
  maxImageCount?: number
  isLookbookImage?: boolean
  currentLookbookIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  modalZoomImage: '',
  modalVideo: '',
  altText: 'Lookbook Image',
  productType: 'pumps',
  maxImageCount: 75,
  currentLookbookIndex: 0
})

const emit = defineEmits<{
  close: []
  updateImage: [{ image: string, video: string }]
  navigateLookbook: ['prev' | 'next']
}>()

const isLookbookImage = computed(() => {
  // Use external prop if provided, otherwise use internal logic
  if (props.isLookbookImage !== undefined) {
    return props.isLookbookImage
  }
  
  // Check if it's a lookbook image
  const isLookbook = (props.modalImage?.includes('/product/') && props.modalImage?.includes('/looks/')) ||
                     (props.modalVideo?.includes('/product/') && props.modalVideo?.includes('/looks/')) ||
                     (props.modalImage?.includes('/k/kendrick-paris/lookbook/')) ||
                     (props.modalVideo?.includes('/k/kendrick-paris/lookbook/')) ||
                     /kendrick-[\w-]+-\d+z?\.(jpg|mp4)$/.test(props.modalImage || props.modalVideo || '')
  
  return isLookbook
})

const currentLookbookIndex = computed(() => {
  // Use external prop if provided, otherwise use internal logic
  if (props.currentLookbookIndex !== undefined) {
    return props.currentLookbookIndex
  }
  if (!isLookbookImage.value) return 0
  const currentPath = props.modalVideo || props.modalImage
  const match = currentPath.match(/kendrick-\w+-\w+-(\d+)\.(jpg|mp4)/)
  return match ? parseInt(match[1]) : 0
})

const closeModal = () => {
  emit('close')
}

// Add a ref to track if navigation is happening
const isNavigating = ref(false)

const navigateLookbook = async (direction: 'prev' | 'next') => {
  
  if (!props.show) return // Guard against unmounted component
  showTips.value = false

  // Track navigation
  if (isLookbookImage.value && props.productType) {
    const { trackLookbookNavigation } = useGA()
    const productColor = props.modalImage?.match(/kendrick-[\w-]+-(\w+)-/)?.[1] || 'unknown'
    trackLookbookNavigation(props.productType, productColor, direction)
  }
  
  // Just emit to parent and let them handle the navigation
  emit('navigateLookbook', direction)
  
  // Set navigating state for visual feedback
  isNavigating.value = true
  setTimeout(() => { 
    if (props.show) { // Only update if still mounted
      isNavigating.value = false 
    }
  }, 300)
}

// --- Zoom logic ---
const zoomed = ref(false)
const showingZ = ref(false)
const zImageExists = ref(false)
const zImagePath = computed(() => {
  if (props.modalZoomImage) return props.modalZoomImage
  if (!props.modalImage) return ''
  const match = props.modalImage.match(/(kendrick-[\w-]+)-(\d+)\.jpg$/)
  if (match) {
    return props.modalImage.replace(/(\d+)\.jpg$/, '$1z.jpg')
  }
  return ''
})

// --- Panning logic ---
const panOffset = ref({ x: 50, y: 50 }) // Pourcentage (0-100)
const zoomLevel = 1.5 // ou 2 pour un zoom plus fort
const hasMovedSinceZoom = ref(false)
const showTips = ref(false)
const tipsTimeout = ref<NodeJS.Timeout | null>(null)

const startTipsTimer = () => {

  if (tipsTimeout.value) {
    clearTimeout(tipsTimeout.value)
    tipsTimeout.value = null
  }
  // Only set timer if modal is still open
  if (!props.show) return
  
  tipsTimeout.value = setTimeout(() => {
    // Double check that modal is still open and component is mounted
    if (!zoomed.value && props.show) {
      showTips.value = true
    }
  }, 5000) // 5 seconds for test
}

const resetTipsTimer = () => {

  if (tipsTimeout.value) {
    clearTimeout(tipsTimeout.value)
    tipsTimeout.value = null
  }
  showTips.value = false
  // Only restart timer if modal is still open
  if (props.show) {
    startTipsTimer()
  }
}

const backgroundStyle = computed(() => {
  if (!zoomed.value) {
    return {
      width: 'auto',
      height: 'calc(100vh - 32px)',
      margin: '16px 0',
      backgroundImage: `url('${displayedImage.value}')`,
      backgroundSize: 'auto 100vh',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      cursor: 'zoom-in',
      transition: 'background-position 0.1s ease-out'
    }
  }
  // Mode zoom/pan
  return {
    width: '100vw',
    height: '100vh',
    backgroundImage: `url('${displayedImage.value}')`,
    backgroundSize: 'cover',
    backgroundPosition: `${panOffset.value.x}% ${panOffset.value.y}%`,
    backgroundRepeat: 'no-repeat',
    cursor: 'grab',
    transition: 'background-position 0.1s ease-out'
  }
})

const pan = (e: MouseEvent) => {
  if (!props.show) return // Guard against unmounted component
  showTips.value = false

  resetTipsTimer()
  if (!hasMovedSinceZoom.value) {
    hasMovedSinceZoom.value = true
    return
  }
  const container = e.currentTarget as HTMLElement
  if (!container) return
  const rect = container.getBoundingClientRect()
  // Position relative de la souris dans le conteneur (0 à 1)
  const relX = (e.clientX - rect.left) / rect.width
  const relY = (e.clientY - rect.top) / rect.height
  // On centre le viewport sur la souris
  panOffset.value = {
    x: relX * 100,
    y: relY * 100
  }
}

const panTouch = (e: TouchEvent) => {
  if (!props.show) return // Guard against unmounted component
  e.preventDefault()
  showTips.value = false

  resetTipsTimer()
  if (!hasMovedSinceZoom.value) {
    hasMovedSinceZoom.value = true
    return
  }
  
  const touch = e.touches[0]
  const container = e.currentTarget as HTMLElement
  if (!container) return
  const rect = container.getBoundingClientRect()
  // Position relative du touch dans le conteneur (0 à 1)
  const relX = (touch.clientX - rect.left) / rect.width
  const relY = (touch.clientY - rect.top) / rect.height
  // On centre le viewport sur le touch
  panOffset.value = {
    x: relX * 100,
    y: relY * 100
  }
}

const handleRightClick = (e: MouseEvent) => {
  e.preventDefault()
  closeModal()
}

const toggleZoom = () => {
  if (!props.show) return // Guard against unmounted component
  showTips.value = false

  resetTipsTimer()
  const wasZoomed = zoomed.value
  if (!zoomed.value) {
    zoomed.value = true
    hasMovedSinceZoom.value = false
    panOffset.value = { x: 50, y: 50 }
    if (zImageExists.value) {
      showingZ.value = true
    } else {
      showingZ.value = false
    }
  } else {
    zoomed.value = false
    showingZ.value = false
    panOffset.value = { x: 50, y: 50 }
  }
  
  // Track zoom
  if (isLookbookImage.value && props.productType) {
    const { trackLookbookZoom } = useGA()
    const productColor = props.modalImage?.match(/kendrick-[\w-]+-(\w+)-/)?.[1] || 'unknown'
    trackLookbookZoom(props.productType, productColor, zoomed.value)
  }
}

const handleWheel = (e: WheelEvent) => {
  if (!zoomed.value) return
  e.preventDefault()
  
  // Optional: Add zoom in/out with wheel
  // This could be implemented here if needed
}

const getImageStyle = () => {
  if (zImageActive.value) {
    const style = {
      width: '150vw', // Plus large que le viewport
      height: '150vh', // Plus haute que le viewport
      display: 'block',
      margin: 'auto',
      transform: `translate(${panOffset.value.x}px, ${panOffset.value.y}px)`,
      cursor: 'grab',
      transition: 'transform 0.1s ease-out',
      transformOrigin: 'center center',
      objectFit: 'cover'
    }
    return style
  } else if (zoomed.value) {
    const style = {
      width: '150vw', // Plus large que le viewport
      height: '150vh', // Plus haute que le viewport
      display: 'block',
      margin: 'auto',
      transform: `translate(${panOffset.value.x}px, ${panOffset.value.y}px)`,
      cursor: 'grab',
      transition: 'transform 0.1s ease-out',
      transformOrigin: 'center center',
      objectFit: 'cover'
    }
    return style
  }
  return ''
}

watch(() => props.modalImage, async () => {
  if (!props.show) return // Guard against unmounted component
  zImageExists.value = false
  if (props.modalZoomImage) {
    zImageExists.value = true
  } else if (zImagePath.value) {
    try {
      const res = await fetch(zImagePath.value, { method: 'HEAD' })
      if (props.show) { // Check again after async operation
        zImageExists.value = res.ok
      }
    } catch {}
  }
  if (props.show) { // Check again before updating state
    zoomed.value = false
    showingZ.value = false
    panOffset.value = { x: 50, y: 50 }
    hasMovedSinceZoom.value = false
  }
})

watch(() => props.show, (newVal) => {
  if (newVal) {
    startTipsTimer()
  } else {
    resetTipsTimer() // This will now only clear, not restart
  }
})

watch(() => showTips.value, (newVal) => {
  // Tips state changed
})

onUnmounted(() => {
  if (tipsTimeout.value) {
    clearTimeout(tipsTimeout.value)
    tipsTimeout.value = null
  }
  // Reset all reactive state to prevent stale references
  showTips.value = false
  zoomed.value = false
  showingZ.value = false
  isNavigating.value = false
})

const displayedImage = computed(() => {
  if (zoomed.value && showingZ.value && zImageExists.value) {
    return zImagePath.value
  }
  return props.modalImage
})



const handleKeydown = (e: KeyboardEvent) => {
  showTips.value = false
  if (e.key === 'Escape') {
    if (zoomed.value) {
      zoomed.value = false
      showingZ.value = false
      panOffset.value = { x: 0, y: 0 }
    } else {
      closeModal()
    }
      } else if (e.key === 'ArrowLeft') {
      navigateLookbook('prev')
    } else if (e.key === 'ArrowRight') {
      navigateLookbook('next')
    }
}

onMounted(() => {
  const handleKeydown = (e: KeyboardEvent) => {
    if (!props.show) return

    if (e.code === 'Space' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault()
      toggleZoom()
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      navigateLookbook('prev')
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      navigateLookbook('next')
    } else if (e.key === 'Escape') {
      e.preventDefault()
      closeModal()
    }
  }

  window.addEventListener('keydown', handleKeydown)

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown)
    // Clear any remaining timeouts
    if (tipsTimeout.value) {
      clearTimeout(tipsTimeout.value)
      tipsTimeout.value = null
    }
  })
})

const zImageActive = computed(() => zoomed.value && showingZ.value && zImageExists.value)

const canPan = computed(() => zoomed.value || zImageActive.value)

// State monitoring
watch([zoomed, zImageActive, canPan], ([newZoomed, newZImageActive, newCanPan]) => {
  // State changed
})
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.4s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}



/* Force full height on mobile */
@media (max-width: 768px) {
  :deep(.absolute.inset-0) {
    height: 100vh !important;
    width: 100vw !important;
  }
  
  :deep(.flex.items-center.justify-center) {
    min-height: 100vh !important;
  }
  
  /* Remove margins on mobile for first image */
  :deep(.modal-image-container) {
    margin: 0 !important;
    height: 100vh !important;
  }
}
</style>
